"use client";

import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { ITeamRecord } from "../interface/ITeamData";
import ui8 from "@/assest/ui8.png";

interface IProps {
  teamData?: ITeamRecord[];
}

const positions = [
  // Central Large Image
  {
    className:
      "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] h-[360px] border-6 z-20",
  },
  // Top Right
  {
    className: "top-[-18%] right-[18%] w-[194px] h-[194px] border-3  z-10",
  },
  // Right Middle
  {
    className: "top-[16%] right-[10%] w-[160px] h-[160px] border-3  z-10",
  },
  // Right Lower
  {
    className: "top-[50%] right-[1%] w-[200px] h-[200px] border-3  z-10",
  },
  // Bottom Right
  {
    className: "bottom-[-8%] right-[19%] w-[160px] h-[160px] border-3 z-10",
  },
  // Bottom Center
  {
    className:
      "bottom-[0%] left-[45%] -translate-x-1/2 w-[123px] h-[123px] border-3 z-10",
  },
  // Bottom Left
  {
    className: "bottom-[10%] left-[14%] w-[135px] h-[135px] border-3  z-10",
  },
  // Left Middle
  {
    className: "top-[37%] left-[0%] w-[172px] h-[172px] border-3 z-10",
  },
  // Top Left
  {
    className: "top-[0%] left-[10%] w-[191px] h-[191px]  border-3 z-10",
  },
  // Top Center
  {
    className: "top-[5%] left-[40%] w-[121px] h-[121px] border-3 z-10",
  },
];

const OurTeam = ({ teamData }: IProps) => {
  const members = teamData ?? [];

  const [positionIndexes, setPositionIndexes] = useState(
    Array.from({ length: 10 }, (_, i) => i)
  );

  const centerMember = members[positionIndexes.indexOf(0)];

  useEffect(() => {
    const interval = setInterval(() => {
      setPositionIndexes((prev) => {
        const newIndexes = [...prev];
        for (let i = newIndexes.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newIndexes[i], newIndexes[j]] = [newIndexes[j], newIndexes[i]];
        }
        return newIndexes;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const [smIndex, setSmIndex] = useState(0);

  // useEffect(() => {
  //   if (!teamData || teamData.length === 0) return;
  //   const interval = setInterval(() => {
  //     setSmIndex((prev) => (prev + 1) % teamData.length);
  //   }, 4000);
  //   return () => clearInterval(interval);
  // }, [teamData]);

  return (
    <section className="py-32 relative">
      <div className="hidden md:flex w-full items-center justify-between gap-20">
        <motion.div
          key={centerMember?.id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-full flex flex-col items-end text-end z-30 "
        >
          <p className="typography-h2-light w-full max-w-[360px] font-extrabold text-grey-200 tracking-[3%] leading-[64px]">
            {centerMember?.name}
          </p>

          <p className="text-[#34445B] typography-sub-h2-semibold font-normal leading-[150%] tracking-[3%] my-6">
            {centerMember?.role}
          </p>
          <p
            className="text-[#818181] text-[18px] leading-[150%] font-normal"
            dangerouslySetInnerHTML={{
              __html: centerMember?.bio || "",
            }}
          ></p>
        </motion.div>

        {/* Image Section */}
        <div className="relative flex-shrink-0 w-[860px] h-[44rem] ">
          {members.map((member, index) => {
            const posIndex = positionIndexes[index];
            const position = positions[posIndex];

            if (!position) return null;

            return (
              <motion.div
                key={member.id}
                layout
                initial={false}
                animate={{
                  zIndex: position.className.includes("z-20") ? 20 : 10,
                }}
                transition={{
                  type: "spring",
                  stiffness: 40,
                  damping: 15,
                  mass: 1,
                }}
                className={`absolute rounded-full overflow-hidden border-[#FFFFFF] shadow-[0px_0px_15px_0px_#1C1D2226] ${position.className}`}
              >
                <Image
                  src={member?.photo || "/placeholder.svg"}
                  alt={member?.name || "Team member"}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      <div className="md:hidden w-full flex flex-col items-center">
        <div className="h-[312px] w-[312px] relative rounded-[100px] overflow-hidden">
          <AnimatePresence mode="wait">
            {members[smIndex] && (
              <motion.div
                key={members[smIndex].id}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <Image
                  src={members[smIndex].photo || "/placeholder.svg"}
                  alt={members[smIndex].name || "Team member"}
                  height={600}
                  width={600}
                  className="object-cover rounded-[100px] w-full h-full"
                  // sizes="100vw"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="w-full max-w-[560px] mt-6 text-center px-4">
          <p className="typography-h5-semibold text-grey-200">
            {members[smIndex]?.name}
          </p>

          <p className="text-[#34445B] typography-mid-body font-medium mt-2">
            {members[smIndex]?.role}
          </p>
          <p
            className="text-[#818181] text-[16px] leading-[150%] font-normal mt-4 typography-small-body"
            dangerouslySetInnerHTML={{ __html: members[smIndex]?.bio || "" }}
          ></p>

          <div className="flex gap-2 justify-center mt-4">
            {members.map((_, i) => (
              <button
                key={i}
                onClick={() => setSmIndex(i)}
                className={`w-2 h-2 rounded-full ${
                  i === smIndex ? "bg-gray-800" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
      <Image
        src={ui8}
        alt="ui accent"
        width={500}
        height={500}
        className="absolute -bottom-16 w-screen z-0 object-cover"
      />
    </section>
  );
};

export default OurTeam;
